import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Grid, Card, CardContent, Typography, Button, Box, Paper, CircularProgress } from '@mui/material';
import errorHandler from './utils/errorHandler';

const LandingPage = () => {
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [showLanding, setShowLanding] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const checkAuthentication = async () => {
      try {
        errorHandler.logDebug('LandingPage', 'Starting authentication check');

        // First, check if user is already authenticated via localStorage or session
        try {
          // Check localStorage for user data and token
          const localUser = localStorage.getItem('user');
          const localToken = localStorage.getItem('access_token');
          
          if (localUser && localToken) {
            try {
              const userData = JSON.parse(localUser);
              if (userData.id && userData.email) {
                errorHandler.logDebug('LandingPage', 'Valid user data found in localStorage, redirecting to dashboard');
                navigate('/dashboard');
                return;
              }
            } catch (parseError) {
              errorHandler.logDebug('LandingPage', 'Invalid user data in localStorage, clearing and continuing');
              localStorage.removeItem('user');
              localStorage.removeItem('access_token');
              localStorage.removeItem('refresh_token');
            }
          }

          // Also check server session with proper JWT header
          const headers = {
            'Content-Type': 'application/json',
          };
          
          if (localToken) {
            headers['Authorization'] = `Bearer ${localToken}`;
          }

          const sessionResponse = await fetch('/api/auth/check-session', {
            method: 'GET',
            credentials: 'include',
            headers: headers
          });

          if (sessionResponse.ok) {
            const sessionData = await sessionResponse.json();
            if (sessionData.success && sessionData.user_id) {
              errorHandler.logDebug('LandingPage', 'Existing session found, redirecting to dashboard');
              navigate('/dashboard');
              return;
            }
          }
        } catch (sessionError) {
          errorHandler.logDebug('LandingPage', 'No existing session found', sessionError);
        }

        // Check for SSO parameters in URL
        const urlParams = new URLSearchParams(window.location.search);
        const authCode = urlParams.get('code');
        const authState = urlParams.get('state');

        if (authCode && authState === 'auth0_sso') {
          errorHandler.logDebug('LandingPage', 'SSO parameters detected, processing SSO login');
          
          try {
            // Let the backend handle SSO verification and redirect
            window.location.href = `/api/auth/sso-entry?code=${authCode}&state=${authState}`;
            return;
          } catch (ssoError) {
            errorHandler.logError('LandingPage', 'SSO processing failed', ssoError);
            // Fall through to show landing page
          }
        }

        // No authentication found and no SSO parameters - show landing page
        errorHandler.logDebug('LandingPage', 'No authentication found, showing landing page');
        setShowLanding(true);
        setIsCheckingAuth(false);

      } catch (error) {
        errorHandler.logError('LandingPage', 'Authentication check failed', error);
        // On error, show landing page
        setShowLanding(true);
        setIsCheckingAuth(false);
      }
    };

    checkAuthentication();
  }, [navigate]);

  // Show loading state while checking authentication
  if (isCheckingAuth) {
    return (
      <Container maxWidth="lg">
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '50vh',
          flexDirection: 'column'
        }}>
          <CircularProgress size={60} sx={{ mb: 2 }} />
          <Typography variant="h6" color="textSecondary">
            The Salon Management System
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Checking authentication...
          </Typography>
        </Box>
      </Container>
    );
  }

  // Show landing page only if no authentication was found
  if (!showLanding) {
    return null;
  }

  errorHandler.logDebug('LandingPage', 'LandingPage rendered');
  
  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h2" align="center" gutterBottom>
          The Salon Management System
        </Typography>
        <Typography variant="subtitle1" align="center" gutterBottom>
          Manage your salon with ease – from finances to staff, holidays to performance reviews, and so much more.
        </Typography>
      </Box>
      <Grid container spacing={4} justifyContent="center">
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h5" gutterBottom>Login</Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Access your account to manage your salon.
              </Typography>
              <Button component={Link} to="/login" variant="contained" color="primary" fullWidth sx={{ mt: 2 }}>
                Login
              </Button>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <CardContent sx={{ flexGrow: 1 }}>
              <Typography variant="h5" gutterBottom>Access via SSO</Typography>
              <Typography variant="body2" color="textSecondary" gutterBottom>
                Access through the main dashboard for seamless login.
              </Typography>
              <Button 
                onClick={() => window.location.href = 'https://app.salonlogicpro.co.uk'}
                variant="contained" 
                color="secondary" 
                fullWidth 
                sx={{ mt: 2 }}
              >
                Go to Dashboard
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Box sx={{ mt: 4, textAlign: 'center' }}>
        <Link to="/password-recovery" style={{ textDecoration: 'none' }}>
          <Typography variant="body2" color="textSecondary">
            Forgot your password?
          </Typography>
        </Link>
      </Box>
      <Paper elevation={3} sx={{ mt: 4, p: 3 }}>
        <Typography variant="h5" gutterBottom>
          About Our Salon Management System
        </Typography>
        <Typography variant="body1">
         Transform your salon into a well-oiled machine with our all-in-one salon management system. Effortlessly handle everything from holiday scheduling and supplier management to employee tracking and financial reporting, all in one user-friendly platform. Designed to simplify your operations, our system frees you to focus on what truly matters—delivering exceptional experiences to your clients. Choose from flexible payment options, including discounted annual plans, and start running your salon smarter, not harder. 
        </Typography>
      </Paper>
    </Container>
  );
};

export default LandingPage;